import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QrCode, User, Wallet, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { PhraseOfDayCard, ShiftReminderCard } from "@/pages/staff/RockTalk";

function StaffArrivals() {
  const [rows, setRows] = useState([]);
  useEffect(() => { const loadA = () => api.get("/staff/arrivals").then((r) => setRows(r.data)).catch(() => {}); loadA(); const t = setInterval(loadA, 20000); return () => clearInterval(t); }, []);
  if (!rows.length) return null;
  const color = { on_way: "text-turquoise", late: "text-amber-400", here: "text-emerald-400" };
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-3" data-testid="staff-arrivals">
      <p className="text-[10px] font-bold text-turquoise tracking-widest uppercase mb-2">Parent pickup alerts (today)</p>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between text-sm">
            <span className="text-white/80">{r.child_name}</span>
            <span className={`font-semibold ${color[r.status] || "text-white/60"}`}>{r.label} · {new Date(r.created_at).toLocaleTimeString("en", { hour: "numeric", minute: "2-digit" })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Scanner() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [token, setToken] = useState("");
  const [scan, setScan] = useState(null);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/staff/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const doScan = async () => {
    setBusy(true);
    try { const { data } = await api.post("/staff/scan", { token }); setScan(data);
      if (data.attendance_marked) toast.success(`${data.first_name} checked in for today`);
      else if (data.has_booking_today) toast.info(`${data.first_name} was already checked in`);
      else toast.warning(`${data.first_name} has NO booking today — flagged for the office to book`);
    }
    catch (e) { toast.error(apiErr(e)); setScan(null); }
    finally { setBusy(false); }
  };

  const buy = async (product_id) => {
    try {
      const { data } = await api.post("/staff/purchase", { token, product_id });
      toast.success(`${data.item} · New balance ${data.balance.toFixed(2)}`);
      setScan({ ...scan, balance: data.balance });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const cats = [...new Set(products.map((p) => p.category))];

  return (
    <div className="min-h-screen bg-navy text-white">
      <header className="h-16 flex items-center justify-between gap-2 px-4 border-b border-white/10">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm hidden sm:block">STAFF SCANNER</p></div>
        <div className="flex items-center gap-1 overflow-x-auto [&>button]:shrink-0">
          <Button variant="ghost" size="sm" onClick={() => nav(["admin", "manager"].includes(user?.role) ? "/admin" : "/dashboard")} className="text-white/70"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/program")} className="text-white/70" data-testid="goto-program-btn">Program</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/photos")} className="text-white/70" data-testid="goto-photos-btn">Photos</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/transport")} className="text-white/70" data-testid="goto-transport-btn">Transport</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/attendance")} className="text-white/70" data-testid="goto-attendance-btn">Register</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/daytime")} className="text-gold" data-testid="goto-rock-crew-btn">Rock Crew</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/rock-talk")} className="text-turquoise" data-testid="goto-rocktalk-btn">Rock Talk</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }} data-testid="scanner-logout">✕</Button>
        </div>
      </header>

      <main className="max-w-md mx-auto p-6">
        <StaffArrivals />
        <PhraseOfDayCard />
        <ShiftReminderCard />
        <div className="text-center mb-6">
          <QrCode className="w-14 h-14 text-turquoise mx-auto mb-2" />
          <h1 className="font-display font-extrabold text-2xl">Scan Gold Card</h1>
          <p className="text-white/50 text-sm">Enter or scan the card's secure token.</p>
        </div>

        <div className="flex gap-2">
          <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Card token" className="rounded-xl bg-white/10 border-white/20 text-white" data-testid="scan-token-input" />
          <Button onClick={doScan} disabled={busy || !token} className="bg-turquoise text-navy font-bold rounded-full" data-testid="scan-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Scan"}</Button>
        </div>

        {scan && (
          <div className="mt-6" data-testid="scan-result">
            <div className="bg-white/5 border border-turquoise/30 rounded-2xl p-5 flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-turquoise/20 flex items-center justify-center overflow-hidden">
                {scan.photo_url ? <img src={scan.photo_url} className="w-full h-full object-cover" alt="" /> : <User className="w-8 h-8 text-turquoise" />}
              </div>
              <div>
                <p className="font-display font-bold text-xl">{scan.first_name}</p>
                <p className="font-mono text-turquoise text-sm">{scan.explorer_id}</p>
                <p className="text-sm text-white/70 flex items-center gap-1 mt-1"><Wallet className="w-4 h-4 text-gold" /> Balance ${scan.balance.toFixed(2)}</p>
              </div>
            </div>

            {scan.has_booking_today && !scan.booking_paid && (
              <p className="mt-4 text-center text-red-200 bg-red-500/15 border border-red-400/30 rounded-xl p-3 text-sm font-semibold" data-testid="scan-unpaid">⚠ Payment not received for today. Payment is due before arrival — please collect payment or have the family pay in the app before check-in.</p>
            )}

            {!scan.snack_enabled ? (
              <p className="mt-4 text-center text-coral bg-coral/10 rounded-xl p-3">Snack purchasing disabled for this explorer.</p>
            ) : (
              <div className="mt-5 space-y-4">
                {cats.map((cat) => (
                  <div key={cat}>
                    <p className="text-xs font-bold text-gold tracking-widest uppercase mb-2">{cat}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {products.filter((p) => p.category === cat).map((p) => (
                        <button key={p.id} onClick={() => buy(p.id)} data-testid={`buy-${p.id}`}
                          className="bg-white/5 hover:bg-turquoise/20 border border-white/10 rounded-xl p-3 text-left transition-colors">
                          <p className="font-semibold text-sm">{p.name}</p>
                          <p className="text-gold font-bold text-sm">${p.price.toFixed(2)}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
